import React, { useEffect, useState } from 'react';
import { MapPin, Wifi, WifiOff, ChevronDown, Menu, Bug } from 'lucide-react';
import { farmService } from '@/services/farmService';
import { FarmRecord } from '@/types/database';
import { useBugReport } from '@/context/BugReportContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface MobileHeaderProps {
  onOpenDrawer: () => void;
  selectedFarmId: string | null;
  onSelectFarm: (farmId: string | null) => void;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  onOpenDrawer,
  selectedFarmId,
  onSelectFarm,
}) => {
  const { openBugReport } = useBugReport();
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    farmService.getActiveFarms().then((data) => {
      setFarms(data);
    });

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const currentFarmName = selectedFarmId
    ? farms.find((f) => f.id === selectedFarmId)?.farm_name || 'Selected Farm'
    : 'All Farms';

  return (
    <header className="lg:hidden h-14 bg-slate-900 text-white flex items-center justify-between px-3 sticky top-0 z-30 shadow-md">
      {/* Brand & Drawer toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenDrawer}
          className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300 hover:text-white"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-1.5">
          <span className="text-base">🐷</span>
          <span className="font-bold text-sm tracking-tight">VSB Farm</span>
        </div>
      </div>

      {/* Center/Right: Farm Selector, Bug Button & Online Status */}
      <div className="flex items-center gap-2">
        {/* Report a bug quick button */}
        <button
          onClick={() => openBugReport()}
          className="p-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-red-400 border border-slate-700 transition-colors"
          title="Report a Bug"
          aria-label="Report a Bug"
        >
          <Bug className="w-3.5 h-3.5" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-xs px-2.5 py-1 rounded-full border border-slate-700 text-slate-200">
              <MapPin className="w-3 h-3 text-emerald-400" />
              <span className="max-w-[100px] truncate font-medium">{currentFarmName}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Select Farm Location</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => onSelectFarm(null)}
              className={selectedFarmId === null ? 'font-bold text-emerald-800 bg-emerald-50' : ''}
            >
              📍 All Farms
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {farms.map((farm) => (
              <DropdownMenuItem
                key={farm.id}
                onClick={() => onSelectFarm(farm.id)}
                className={selectedFarmId === farm.id ? 'font-bold text-emerald-800 bg-emerald-50' : ''}
              >
                {farm.farm_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Online / Offline status badge */}
        <div
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            isOnline ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-red-950 text-red-300 border border-red-800'
          }`}
          title={isOnline ? 'Connected to remote database' : 'Offline'}
        >
          {isOnline ? (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="hidden sm:inline">Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3 text-red-400" />
              <span>Offline</span>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

